import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const StartupWizardContext = createContext();

export const useStartupWizard = () => {
  const context = useContext(StartupWizardContext);
  if (!context) {
    throw new Error('useStartupWizard must be used within a StartupWizardProvider');
  }
  return context;
};

export const StartupWizardProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if wizard should be shown for the current user
  useEffect(() => {
    const checkWizardStatus = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const shouldShowWizard = !userData.startupWizardCompleted && !userData.dontShowStartupWizard;
          setShowWizard(shouldShowWizard);
          setWizardCompleted(userData.startupWizardCompleted || false);
          setDontShowAgain(userData.dontShowStartupWizard || false);
        } else {
          // New user, show wizard
          setShowWizard(true);
        }
      } catch (error) {
        console.error('Error checking wizard status:', error);
        // Default to showing wizard on error
        setShowWizard(true);
      } finally {
        setLoading(false);
      }
    };

    checkWizardStatus();
  }, [currentUser]);

  // Mark wizard as completed
  const completeWizard = async (skipFuture = false) => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      if (skipFuture) {
        // User chose not to show again
        await updateDoc(userDocRef, {
          startupWizardCompleted: true,
          dontShowStartupWizard: true,
          startupWizardCompletedAt: Date.now()
        });
        setDontShowAgain(true);
      } else {
        // User completed wizard normally
        await updateDoc(userDocRef, {
          startupWizardCompleted: true,
          startupWizardCompletedAt: Date.now()
        });
      }
      
      setWizardCompleted(true);
      setShowWizard(false);
    } catch (error) {
      console.error('Error completing wizard:', error);
    }
  };

  // Reset wizard (for testing or admin purposes)
  const resetWizard = async () => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        startupWizardCompleted: false,
        dontShowStartupWizard: false,
        startupWizardCompletedAt: null
      });
      
      setWizardCompleted(false);
      setDontShowAgain(false);
      setShowWizard(true);
    } catch (error) {
      console.error('Error resetting wizard:', error);
    }
  };

  // Temporarily hide wizard (for skip/close actions)
  const hideWizard = () => {
    setShowWizard(false);
  };

  const value = {
    showWizard,
    wizardCompleted,
    dontShowAgain,
    loading,
    completeWizard,
    resetWizard,
    hideWizard
  };

  return (
    <StartupWizardContext.Provider value={value}>
      {children}
    </StartupWizardContext.Provider>
  );
};
